/**
 * Jun-2026 (User-Wunsch) — „Aufgaben & Hinweise" als nebeneinander liegende
 * Tabs statt untereinander gestapelter Blöcke:
 *   • Meine Aufgaben (mir zugewiesen)      → MyTasks
 *   • Was zu tun ist (automatische Hinweise) → SmartInbox (+ Empfehlungs-Box B2B)
 *   • Mitarbeiter-Auslastung (nur B2B)      → TeamWorkloadCard
 *
 * Jun-2026 (· 259, User-Wunsch): Jeder Tab zeigt die Anzahl der zu
 * bearbeitenden Vorgänge als Badge und ist farblich unterschiedlich markiert
 * (Aufgaben = Blau, Hinweise = Gelb, Auslastung = Violett; überfällig → Rot).
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { UserCheck, Sparkles, Users, MessageSquare, Inbox, Handshake, Info, Car, Mail, MailCheck, BarChart3, Wrench, Truck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import MyTasks from "./MyTasks";
import SmartInbox from "./SmartInbox";
import TeamWorkloadCard from "./TeamWorkloadCard";
import TeamTimeOverview from "./TeamTimeOverview";
import EmployeeAccessRequestsCard from "./EmployeeAccessRequestsCard";
import DealerReferralProposalsPanel from "./DealerReferralProposalsPanel";
import MyLeadsCard from "./MyLeadsCard";
import MySalesTab from "./MySalesTab";
import B2BDealsPanel from "../dealer/B2BDealsPanel";
import { MyMessages } from "./DashboardTabs";
import { EmailCenterPanel, EmailApprovalPanel } from "./EmailQuickPanels";
import PotentialCard from "./PotentialCard";
import DailyKpisRow from "./DailyKpisRow";

// Farbschema je Tab (aktiver Zustand + Zähl-Badge). Überfällige Vorgänge
// überschreiben die Badge-Farbe mit Rot.
const TAB_THEME = {
    tasks: {
        active: "data-[state=active]:bg-blue-600 data-[state=active]:text-white",
        badge: "bg-blue-600 text-white",
    },
    leads: {
        active: "data-[state=active]:bg-emerald-700 data-[state=active]:text-white",
        badge: "bg-emerald-700 text-white",
    },
    hints: {
        active: "data-[state=active]:bg-[#FFCC00] data-[state=active]:text-[#111827]",
        badge: "bg-[#FFCC00] text-[#111827]",
    },
    workload: {
        active: "data-[state=active]:bg-purple-600 data-[state=active]:text-white",
        badge: "bg-purple-600 text-white",
    },
    messages: {
        active: "data-[state=active]:bg-teal-700 data-[state=active]:text-white",
        badge: "bg-teal-700 text-white",
    },
    sales: {
        active: "data-[state=active]:bg-rose-700 data-[state=active]:text-white",
        badge: "bg-rose-700 text-white",
    },
    workshop: {
        active: "data-[state=active]:bg-amber-700 data-[state=active]:text-white",
        badge: "bg-amber-700 text-white",
    },
    transports: {
        active: "data-[state=active]:bg-lime-700 data-[state=active]:text-white",
        badge: "bg-lime-700 text-white",
    },
    b2b: {
        active: "data-[state=active]:bg-orange-600 data-[state=active]:text-white",
        badge: "bg-orange-600 text-white",
    },
    email: {
        active: "data-[state=active]:bg-cyan-700 data-[state=active]:text-white",
        badge: "bg-cyan-700 text-white",
    },
    emailApproval: {
        active: "data-[state=active]:bg-indigo-700 data-[state=active]:text-white",
        badge: "bg-indigo-700 text-white",
    },
    analytics: {
        active: "data-[state=active]:bg-slate-700 data-[state=active]:text-white",
        badge: "bg-slate-700 text-white",
    },
};

// Jun-2026 (User-Wunsch) — Jeder Tab erhält ein „ⓘ"-Icon, über das man
// nachlesen kann, welche Informationen im jeweiligen Tab angezeigt werden.
// Die Texte sind rollenspezifisch (Händler/Hauptsitz vs. Mitarbeiter sowie
// B2C/Vermittler/Verein), damit jede Rolle nur die für sie relevante
// Erklärung sieht.
const TAB_INFO_BY_ROLE = {
    b2b: {
        tasks: "Quelle: Aufgaben-Modul. Hier erscheinen dir persönlich zugewiesene/delegierte Aufgaben inkl. Fälligkeiten (überfällige rot).",
        leads: "Quelle: Meine Leads (Lead-Datenbank). Enthält importierte Leads, Gast-Leads sowie Probefahrt-Nachverfolgungen (Ergebnis unentschieden / nicht erschienen).",
        b2b: "Quelle: Ankauf-/B2B-Modul. Ankaufsanfragen von Privatkunden und Händler-zu-Händler-Anfragen/-Angebote auf deine Inserate.",
        hints: "Quelle: automatische Hinweise aus mehreren Bereichen — Preisvorschläge (Angebote), eingehende Reservierungen, Probefahrt-Anfragen, neue Gast-Leads, ungelesene Nachrichten, Werkstatt-Termine, fehlende Dokumente, Fristen (TÜV/Skonto) u. a.",
        workload: "Quelle: Werkstatt- & Verkaufsaufträge deiner Mitarbeiter (offen/überfällig) sowie offene Zugriffsanfragen von Mitarbeitern.",
        messages: "Quelle: Nachrichten-Postfach. Ungelesene Konversationen mit Kunden, Händlern und Mitarbeitern.",
        sales: "Quelle: Disposition. Alle Fahrzeuge, bei denen du als Verkäufer hinterlegt bist — mit Suche und Direkt-Sprung in den Verkaufsprozess (Dispo).",
        email: "Quelle: E-Mail-Center (verbundene Postfächer). Zeigt deine Konten mit ungelesenen E-Mails — mit Direkt-Sprung ins E-Mail-Center.",
        emailApproval: "Quelle: E-Mail-Postausgang mit Freigabepflicht. Ausgehende E-Mails warten hier auf Prüfung — direkt freigeben & senden oder verwerfen.",
        analytics: "Quelle: Auswertungs-Modul. Tages-KPIs (Verkäufe heute, offene Rechnungen, Werkstatt-Auslastung) und Potenziale — offener, noch nicht abgerechneter Umsatz — mit Direkt-Sprung in die jeweilige Auswertung.",
    },
    zweiradverkaeufer: {
        tasks: "Quelle: Aufgaben-Modul deines Betriebs. Dir zugewiesene Aufgaben inkl. Fälligkeiten (überfällige rot).",
        leads: "Quelle: Meine Leads deines Betriebs. Dir zugewiesene Interessenten/Anfragen inkl. Gast-Leads & Probefahrt-Nachverfolgung.",
        b2b: "Quelle: Ankauf-/B2B-Anfragen, die deinen Betrieb betreffen.",
        hints: "Quelle: automatische Hinweise zu deinen Vorgängen — Preisvorschläge, Reservierungen, Probefahrt-Anfragen, Gast-Leads, ungelesene Nachrichten, Termine und Fristen.",
        workload: "Quelle: offene/überfällige Aufträge des Teams in deinem Betrieb.",
        messages: "Quelle: Nachrichten-Postfach. Ungelesene Konversationen mit Kunden und Kolleg:innen.",
        sales: "Quelle: Disposition. Alle dir zugeordneten Fahrzeugverkäufe — mit Suche und Direkt-Sprung in den Verkaufsprozess (Dispo).",
        email: "Quelle: E-Mail-Center deines Betriebs. Zeigt für dich freigeschaltete Postfächer mit ungelesenen E-Mails.",
        emailApproval: "Quelle: E-Mail-Postausgang mit Freigabepflicht (nur für Berechtigte sichtbar).",
    },
    b2c: {
        tasks: "Quelle: Aufgaben-Modul. Deine persönlichen To-Dos rund um Kauf, Verkauf und Anfragen.",
        leads: "Quelle: Anfragen/Interessenten zu deinen privaten Inseraten.",
        b2b: "Quelle: Händler-Anfragen zu deinen Inseraten.",
        hints: "Quelle: automatische Hinweise — neue Nachrichten, Antworten auf deine Anfragen, offene Schritte.",
        workload: "Quelle: Übersicht offener Vorgänge.",
        messages: "Quelle: Nachrichten-Postfach. Ungelesene Konversationen.",
    },
    vermittler: {
        tasks: "Quelle: Aufgaben-Modul. Dir zugewiesene Vermittler-Aufgaben und Fälligkeiten.",
        leads: "Quelle: Meine Leads. Dir zugewiesene Leads zum Vermitteln und Nachhalten.",
        b2b: "Quelle: Anfragen, die deine Vermittlungen betreffen.",
        hints: "Quelle: automatische Hinweise — neue Leads, fällige Rückmeldungen, offene Provisions-Vorgänge.",
        workload: "Quelle: Übersicht deiner offenen Vermittlungen.",
        messages: "Quelle: Nachrichten-Postfach. Ungelesene Konversationen.",
    },
    verein: {
        tasks: "Quelle: Aufgaben-Modul. Aufgaben und Fälligkeiten rund um deinen Verein.",
        leads: "Quelle: Anfragen/Interessenten, die deinen Verein betreffen.",
        b2b: "Quelle: Anfragen von Händlern/Partnern an deinen Verein.",
        hints: "Quelle: automatische Hinweise — neue Nachrichten, Mitglieder-Anfragen, offene Vereins-Vorgänge.",
        workload: "Quelle: Übersicht offener Vereins-Vorgänge.",
        messages: "Quelle: Nachrichten-Postfach. Ungelesene Konversationen.",
    },
};

function tabInfoFor(role) {
    return TAB_INFO_BY_ROLE[role] || TAB_INFO_BY_ROLE.b2b;
}

function TabInfo({ text, testId }) {
    const stop = (e) => e.stopPropagation();
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onClick={stop}
                    onPointerDown={stop}
                    className="inline-flex items-center justify-center w-6 h-7 -ml-1 mr-0.5 text-gray-400 hover:text-[#111827] shrink-0"
                    title="Was wird hier angezeigt?"
                    aria-label="Info: Was wird hier angezeigt?"
                    data-testid={testId}
                >
                    <Info className="w-3.5 h-3.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" onClick={stop} data-testid={`${testId}-content`} className="w-72 text-sm leading-relaxed text-gray-700">
                {text}
            </PopoverContent>
        </Popover>
    );
}

function CountBadge({ value, theme, urgent, testId }) {
    if (value === null || value === undefined) return null;
    return (
        <span
            className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-mono-num font-bold ${urgent ? "bg-red-600 text-white" : theme.badge}`}
            data-testid={testId}
        >
            {value}
        </span>
    );
}

export default function TasksHintsTabs({ isB2b }) {
    const { user } = useAuth();
    const TAB_INFO = tabInfoFor(user?.role);
    // Jul-2026 (User-Wunsch) — Mitarbeiter (zweiradverkaeufer) sehen „Meine Leads"
    // und „Meine Verkäufe" ebenfalls unter „Aufgaben & Hinweise".
    const isEmployee = user?.role === "zweiradverkaeufer";
    const showLeadsSales = isB2b || isEmployee;
    // Jul-2026 (User-Wunsch) — Deep-Link ``/dashboard?tab=sales`` (z. B. aus der
    // Disposition: Klick auf den Verkäufer springt zu „Meine Verkäufe").
    const [searchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get("tab") || "tasks");
    // Aug-2026 (Bugfix): Briefing-Chips navigieren nur per Query (?tab=…) auf
    // derselben Seite — lokalen Tab-State mit der URL synchron halten.
    useEffect(() => {
        const t = searchParams.get("tab");
        if (t) setTab(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    const [counts, setCounts] = useState({ tasks: null, tasksOverdue: 0, hints: null, workload: null, workloadOverdue: 0, workloadAccess: 0, messages: null, leads: null, b2b: null, sales: null, email: null, emailApproval: null });

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const reqs = [
                    api.get("/me/tasks").catch(() => ({ data: [] })),
                    api.get("/dashboard/inbox").catch(() => ({ data: { items: [] } })),
                    api.get("/messages/threads").catch(() => ({ data: [] })),
                    // E-Mail-Center: auch Mitarbeiter haben eigene Postfächer.
                    api.get("/email/accounts").catch(() => ({ data: { items: [] } })),
                ];
                if (isB2b) reqs.push(api.get("/dealer/tasks/workload").catch(() => ({ data: { items: [] } })));
                else reqs.push(Promise.resolve({ data: { items: [] } }));
                if (showLeadsSales) reqs.push(api.get("/dealer/leads", { params: isEmployee ? { mine: 1 } : {} }).catch(() => ({ data: { counts: {} } })));
                else reqs.push(Promise.resolve({ data: { counts: {} } }));
                if (isB2b) reqs.push(api.get("/dealer/access-requests").catch(() => ({ data: { items: [] } })));
                else reqs.push(Promise.resolve({ data: { items: [] } }));
                if (isB2b) reqs.push(api.get("/dealer/b2b/deals?direction=incoming").catch(() => ({ data: { items: [] } })));
                else reqs.push(Promise.resolve({ data: { items: [] } }));
                if (showLeadsSales) reqs.push(api.get("/dealer/my-sales").catch(() => ({ data: { items: [] } })));
                else reqs.push(Promise.resolve({ data: { items: [] } }));
                if (isB2b) reqs.push(api.get("/dealer/email-outbox", { params: { status: "pending", limit: 1 } }).catch(() => ({ data: null })));
                else reqs.push(Promise.resolve({ data: null }));
                const [tasksR, inboxR, threadsR, emailR, workloadR, leadsR, accessR, b2bR, salesR, outboxR] = await Promise.all(reqs);
                if (cancelled) return;
                const tasks = Array.isArray(tasksR.data) ? tasksR.data : (tasksR.data?.items || []);
                const overdue = tasks.filter((t) => t.overdue).length;
                const inboxItems = inboxR.data?.items || [];
                const threads = Array.isArray(threadsR.data) ? threadsR.data : (threadsR.data?.items || []);
                const unread = threads.reduce((s, t) => s + (t.unread || 0), 0);
                const wlRows = workloadR?.data?.items || [];
                const wlOpen = wlRows.reduce((s, r) => s + (r.open || 0), 0);
                const wlOverdue = wlRows.reduce((s, r) => s + (r.overdue || 0), 0);
                const accessCount = (accessR?.data?.items || []).length;
                const lc = leadsR?.data?.counts || {};
                const leadsActive = (lc.assigned || 0) + (lc.in_progress || 0);
                const b2bOpen = (b2bR?.data?.items || []).filter((d) => d.status === "open").length;
                // Jul-2026 (User-Wunsch): Verkaufs-Anzahl auch für Mitarbeiter anzeigen.
                const salesCount = showLeadsSales ? (salesR?.data?.count ?? (salesR?.data?.items || []).length) : null;
                const emailUnread = (emailR?.data?.items || []).reduce((s, a) => s + (a.unread || 0), 0);
                const outboxPending = isB2b && outboxR?.data ? (outboxR.data.counts?.pending ?? outboxR.data.total ?? 0) : null;
                setCounts({
                    tasks: tasks.length,
                    tasksOverdue: overdue,
                    hints: inboxR.data?.total ?? inboxItems.length,
                    workload: isB2b ? wlOpen + accessCount : null,
                    workloadOverdue: wlOverdue,
                    workloadAccess: accessCount,
                    messages: unread,
                    leads: showLeadsSales ? leadsActive : null,
                    b2b: isB2b ? b2bOpen : null,
                    sales: salesCount,
                    email: emailUnread,
                    emailApproval: outboxPending,
                });
            } catch { /* ignore */ }
        };
        load();
        return () => { cancelled = true; };
    }, [isB2b, isEmployee]);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="mb-6" data-testid="tasks-and-hints">
            <h2 className="font-display font-black text-xl text-[#111827] mb-3">Aufgaben &amp; Hinweise</h2>
            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="inline-flex h-auto gap-1 bg-white border border-gray-200 rounded-sm p-1 flex-wrap mb-3" data-testid="tasks-hints-tablist">
                    {/* Jun-2026 (User-Wunsch): feste Tab-Reihenfolge — Aufgaben, Was zu tun ist,
                        Leads, E-Mail-Center, Nachrichten, Auslastung, B2B, Verkäufe, Freigabe. */}
                    <TabsTrigger value="tasks" title={TAB_INFO.tasks} data-testid="tasks-hints-tab-tasks" className={TAB_THEME.tasks.active}>
                        <UserCheck className="w-4 h-4 mr-1.5" /> Meine Aufgaben
                        <CountBadge value={counts.tasks} theme={TAB_THEME.tasks} urgent={counts.tasksOverdue > 0} testId="tasks-hints-count-tasks" />
                    </TabsTrigger>
                    <TabsTrigger value="hints" title={TAB_INFO.hints} data-testid="tasks-hints-tab-hints" className={TAB_THEME.hints.active}>
                        <Sparkles className="w-4 h-4 mr-1.5" /> Was zu tun ist
                        <CountBadge value={counts.hints} theme={TAB_THEME.hints} urgent={false} testId="tasks-hints-count-hints" />
                    </TabsTrigger>
                    {showLeadsSales && (
                        <TabsTrigger value="leads" title={TAB_INFO.leads} data-testid="tasks-hints-tab-leads" className={TAB_THEME.leads.active}>
                            <Inbox className="w-4 h-4 mr-1.5" /> Meine Leads
                            <CountBadge value={counts.leads} theme={TAB_THEME.leads} urgent={false} testId="tasks-hints-count-leads" />
                        </TabsTrigger>
                    )}
                    {/* E-Mail-Center: auch für Mitarbeiter (eigenes Postfach). */}
                    <TabsTrigger value="email" title={TAB_INFO.email} data-testid="tasks-hints-tab-email" className={TAB_THEME.email.active}>
                        <Mail className="w-4 h-4 mr-1.5" /> E-Mail-Center
                        <CountBadge value={counts.email} theme={TAB_THEME.email} urgent={false} testId="tasks-hints-count-email" />
                    </TabsTrigger>
                    {/* Nachrichten: für Mitarbeiter ausgeblendet (User-Wunsch Jul-2026). */}
                    {!isEmployee && (
                        <TabsTrigger value="messages" title={TAB_INFO.messages} data-testid="tasks-hints-tab-messages" className={TAB_THEME.messages.active}>
                            <MessageSquare className="w-4 h-4 mr-1.5" /> Nachrichten
                            <CountBadge value={counts.messages} theme={TAB_THEME.messages} urgent={false} testId="tasks-hints-count-messages" />
                        </TabsTrigger>
                    )}
                    {isB2b && (
                        <TabsTrigger value="workload" title={TAB_INFO.workload} data-testid="tasks-hints-tab-workload" className={TAB_THEME.workload.active}>
                            <Users className="w-4 h-4 mr-1.5" /> Mitarbeiter-Auslastung
                            <CountBadge value={counts.workload} theme={TAB_THEME.workload} urgent={counts.workloadOverdue > 0} testId="tasks-hints-count-workload" />
                        </TabsTrigger>
                    )}
                    {isB2b && (
                        <TabsTrigger value="b2b" title={TAB_INFO.b2b} data-testid="tasks-hints-tab-b2b" className={TAB_THEME.b2b.active}>
                            <Handshake className="w-4 h-4 mr-1.5" /> B2B-Anfragen
                            <CountBadge value={counts.b2b} theme={TAB_THEME.b2b} urgent={false} testId="tasks-hints-count-b2b" />
                        </TabsTrigger>
                    )}
                    {showLeadsSales && (
                        <TabsTrigger value="sales" title={TAB_INFO.sales} data-testid="tasks-hints-tab-sales" className={TAB_THEME.sales.active}>
                            <Car className="w-4 h-4 mr-1.5" /> Meine Verkäufe
                            <CountBadge value={counts.sales} theme={TAB_THEME.sales} urgent={false} testId="tasks-hints-count-sales" />
                        </TabsTrigger>
                    )}
                    {isB2b && (
                        <TabsTrigger value="emailApproval" title={TAB_INFO.emailApproval} data-testid="tasks-hints-tab-email-approval" className={TAB_THEME.emailApproval.active}>
                            <MailCheck className="w-4 h-4 mr-1.5" /> E-Mail-Freigabe
                            <CountBadge value={counts.emailApproval} theme={TAB_THEME.emailApproval} urgent={(counts.emailApproval || 0) > 0} testId="tasks-hints-count-email-approval" />
                        </TabsTrigger>
                    )}
                    {isB2b && (
                        <TabsTrigger value="analytics" title={TAB_INFO.analytics} data-testid="tasks-hints-tab-analytics" className={TAB_THEME.analytics.active}>
                            <BarChart3 className="w-4 h-4 mr-1.5" /> Auswertungen
                        </TabsTrigger>
                    )}
                </TabsList>
                <TabsContent value="tasks" data-testid="tasks-hints-content-tasks">
                    <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.tasks} testId="tasks-hints-info-tasks" /></div>
                    <MyTasks />
                </TabsContent>
                {showLeadsSales && (
                    <TabsContent value="leads" data-testid="tasks-hints-content-leads">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.leads} testId="tasks-hints-info-leads" /></div>
                        <MyLeadsCard user={user} />
                    </TabsContent>
                )}
                {isB2b && (
                    <TabsContent value="b2b" data-testid="tasks-hints-content-b2b">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.b2b} testId="tasks-hints-info-b2b" /></div>
                        <B2BDealsPanel
                            onOpenCountChange={(n) => setCounts((c) => (c.b2b === n ? c : { ...c, b2b: n }))}
                        />
                    </TabsContent>
                )}
                <TabsContent value="hints" data-testid="tasks-hints-content-hints">
                    <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.hints} testId="tasks-hints-info-hints" /></div>
                    <SmartInbox />
                    {isB2b && (
                        <div className="mt-4">
                            <DealerReferralProposalsPanel />
                        </div>
                    )}
                </TabsContent>
                {isB2b && (
                    <TabsContent value="workload" data-testid="tasks-hints-content-workload">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.workload} testId="tasks-hints-info-workload" /></div>
                        <EmployeeAccessRequestsCard onChange={() => setCounts((c) => ({ ...c, workloadAccess: Math.max(0, (c.workloadAccess || 0) - 1), workload: Math.max(0, (c.workload || 0) - 1) }))} />
                        {/* Jun-2026 (User-Wunsch) — Anwesenheit in die Auslastungs-Karte integriert */}
                        <TeamWorkloadCard />
                    </TabsContent>
                )}
                {!isEmployee && (
                    <TabsContent value="messages" data-testid="tasks-hints-content-messages">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.messages} testId="tasks-hints-info-messages" /></div>
                        <MyMessages currentUser={user} />
                    </TabsContent>
                )}
                {showLeadsSales && (
                    <TabsContent value="sales" data-testid="tasks-hints-content-sales">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.sales} testId="tasks-hints-info-sales" /></div>
                        <MySalesTab />
                    </TabsContent>
                )}
                <TabsContent value="email" data-testid="tasks-hints-content-email">
                    <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.email} testId="tasks-hints-info-email" /></div>
                    <EmailCenterPanel />
                </TabsContent>
                {isB2b && (
                    <TabsContent value="emailApproval" data-testid="tasks-hints-content-email-approval">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.emailApproval} testId="tasks-hints-info-email-approval" /></div>
                        <EmailApprovalPanel onCountChange={(n) => setCounts((c) => (c.emailApproval === n ? c : { ...c, emailApproval: n }))} />
                    </TabsContent>
                )}
                {isB2b && (
                    <TabsContent value="analytics" data-testid="tasks-hints-content-analytics">
                        <div className="flex justify-end mb-1"><TabInfo text={TAB_INFO.analytics} testId="tasks-hints-info-analytics" /></div>
                        <DailyKpisRow />
                        <PotentialCard />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
